class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.bindTitleScopedSubmit(this.input.form);
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }

  bindTitleScopedSubmit(form) {
    // Multiple search components can wrap the same form (for example the
    // predictive search and the search-template component). Bind once so a
    // normal text search is rewritten only once before Shopify handles it.
    if (!form || form.dataset.titleScopedSearchBound === 'true') return;
    form.dataset.titleScopedSearchBound = 'true';
    form.addEventListener('submit', SearchForm.scopeNormalSearchToTitle);
  }

  static scopeNormalSearchToTitle(event) {
    const form = event.currentTarget;
    if (form.querySelector('[aria-selected="true"] a')) return;
    const input = form.querySelector('input[name="q"]');
    if (!input) return;

    const originalTerm = input.value.trim();
    if (!originalTerm || SearchForm.isAdvancedSearch(originalTerm) || SearchForm.isPartNumber(originalTerm)) return;

    const scopedTerm = SearchForm.buildTitleQuery(originalTerm);
    if (scopedTerm === originalTerm) return;

    // Submit Shopify's scoped query through a hidden field so the visible
    // search box never flashes the internal `title:` syntax.
    const queryInput = document.createElement('input');
    queryInput.type = 'hidden';
    queryInput.name = 'q';
    queryInput.value = scopedTerm;
    form.appendChild(queryInput);

    input.name = 'search_display';

    // Restore the form if another listener cancels navigation.
    window.setTimeout(() => {
      input.name = 'q';
      queryInput.remove();
    }, 0);
  }

  static isAdvancedSearch(term) {
    return /(?:^|\s)(?:title|variants\.sku|vendor|product_type|tag):/i.test(term);
  }

  static isPartNumber(term) {
    // Keep one-token SKU/MPN searches on Shopify's existing SKU/MPN path.
    return !/\s/.test(term) && /^(?=.*\d)[a-z0-9][a-z0-9._\/-]*$/i.test(term);
  }

  static buildTitleQuery(term) {
    return term
      .match(/"[^"\n]+"|'[^'\n]+'|\S+/g)
      .map((token) => {
        const value = token.replace(/^("|'|)(.*?)(\1)$/, '$2');
        if (/^[a-z0-9_-]+$/i.test(value)) return `title:${value}`;
        return `title:"${value.replace(/"/g, '\\"')}"`;
      })
      .join(' ');
  }
}

customElements.define('search-form', SearchForm);
